import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ListChecks, Filter } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { TASK_SOURCE_META, PRIORITY_BADGE, timeAgo } from "@/pages/clinical/constants";

function Kpi({ label, value, accent, testId }) {
  return (
    <div className="hah-card p-3" data-testid={testId}>
      <div className="text-2xl font-bold text-[#1F2937]">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]" style={{ color: accent }}>{label}</div>
    </div>
  );
}

export default function ClinicalTasks() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({ by_source: {} });
  const [source, setSource] = useState("all");
  const [priority, setPriority] = useState("all");
  const [mine, setMine] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (source !== "all") p.source = source;
      if (priority !== "all") p.priority = priority;
      if (mine) p.mine = true;
      const r = await api.get("/clinical-ops/tasks", { params: p });
      setItems(r.data.items || []);
      setKpis(r.data.kpis || { by_source: {} });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [source, priority, mine]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="clinical-tasks">
      <PageHeader title="Clinical Tasks" subtitle="Unified view across RPM, Hotline, Teleconsultations and Home Visits."
        actions={
          <Button onClick={() => setMine(!mine)} variant={mine ? "default" : "outline"}
            className={mine ? "bg-[#0B7E8C] hover:bg-[#128C97] text-white" : "border-[#E5E7EB]"} data-testid="ct-mine-toggle">
            <ListChecks className="w-4 h-4 mr-1.5" /> {mine ? "Showing My Tasks" : "Show My Tasks"}
          </Button>
        } />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <Kpi label="Total" value={kpis.total} testId="ct-kpi-total" />
        <Kpi label="Mine" value={kpis.mine} accent="#0B7E8C" />
        <Kpi label="Critical / Urgent" value={kpis.critical} accent="#EF4444" />
        <Kpi label="RPM" value={kpis.by_source?.rpm ?? 0} accent="#8B5CF6" />
        <Kpi label="Orders + MAR" value={(kpis.by_source?.order ?? 0) + (kpis.by_source?.mar ?? 0)} accent="#3B82F6" />
        <Kpi label="Teleconsult" value={kpis.by_source?.teleconsult ?? 0} accent="#0B7E8C" />
      </div>

      <div className="hah-card p-3 mb-3 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-[#6B7280]" />
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-44" data-testid="ct-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="rpm">RPM</SelectItem>
            <SelectItem value="hotline">Hotline</SelectItem>
            <SelectItem value="teleconsult">Teleconsult</SelectItem>
            <SelectItem value="home_visit">Home Visit</SelectItem>
            <SelectItem value="order">Order (Pending Signature)</SelectItem>
            <SelectItem value="mar">MAR (Overdue Doses)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-44" data-testid="ct-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="routine">Routine</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="hah-card overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left p-2.5">Source</th>
              <th className="text-left p-2.5">Task</th>
              <th className="text-left p-2.5">Patient</th>
              <th className="text-left p-2.5">Priority</th>
              <th className="text-left p-2.5">Status</th>
              <th className="text-left p-2.5">Due</th>
              <th className="text-right p-2.5">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-sm text-[#6B7280]">No tasks match your filters.</td></tr>}
            {items.map((t) => {
              const meta = TASK_SOURCE_META[t.source] || { label: t.source, icon: ListChecks, color: "#94A3B8" };
              const Icon = meta.icon;
              const pri = PRIORITY_BADGE[(t.priority || "routine").toLowerCase()] || PRIORITY_BADGE.routine;
              return (
                <tr key={t.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`ct-row-${t.id}`}>
                  <td className="p-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: meta.color }}>
                      <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </span>
                  </td>
                  <td className="p-2.5 font-medium text-[#1F2937]">{t.title}</td>
                  <td className="p-2.5">
                    {t.patient_id ? (
                      <button onClick={() => navigate(`/clinical/patients/${t.patient_id}`)} className="text-left hover:text-[#0B7E8C]">
                        <span>{t.patient_name}</span> <span className="text-xs text-[#94A3B8] font-mono">{t.patient_mrn}</span>
                      </button>
                    ) : <span className="text-[#94A3B8]">—</span>}
                  </td>
                  <td className="p-2.5">
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: pri.bg, color: pri.text }}>
                      {t.priority}
                    </span>
                  </td>
                  <td className="p-2.5 text-[#6B7280] capitalize">{(t.status || "").replace(/_/g, " ")}</td>
                  <td className="p-2.5 text-[#6B7280]">{t.due_at ? new Date(t.due_at).toLocaleString() : "—"} <span className="text-[10px] text-[#94A3B8]">({timeAgo(t.due_at)})</span></td>
                  <td className="p-2.5 text-right">
                    {t.link && (
                      <Button size="sm" onClick={() => navigate(t.link)} className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`ct-open-${t.id}`}>
                        Open
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
